import { IonSegment, IonSlide, IonSlides, IonToolbar } from '@ionic/react';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { TabButton } from './TabButton';
import { useMediaQuery } from 'react-responsive';

import './Tabs.css';
import { useLogger } from '../../services/LogService';

export interface TabData {
  label: string;
  link: string;
  component: ReactElement;
}

interface TabProps extends RouteComponentProps {
  id?: string;
  tabs: TabData[];
  showTab?: boolean;
  classT?: string;
  scrollable?: boolean;
  onCurrentTab?: (tab: TabData) => void;
}

/**
 * Tabs with slidable content
 * @param param0
 * @returns
 */
export const Tabs: React.FC<TabProps> = ({
  id,
  tabs,
  classT = '',
  history,
  scrollable,
  onCurrentTab,
}) => {
  const logger = useLogger('Tabs');

  const findTab = (pathname: string) => {
    logger.debug(`useEffect() findTab() = ${pathname}`, tabs);
    return tabs.findIndex((it) => {
      return pathname.startsWith(it.link);
    });
  };

  const openTab = (tabIdx: number) => {
    logger.info(`openTab() ${tabIdx}`);
    setValue(tabIdx);
    slider.current!.slideTo(tabIdx);
  };

  // a state value to bind segment value
  const [value, setValue] = useState(0);

  useEffect(() => {
    logger.info('useEffect()');
    onCurrentTab && onCurrentTab(tabs[value]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tabs.length > 0) {
      const tabIdx = findTab(history.location.pathname);

      logger.info(
        `useEffect() [history.location.pathname] = ${history.location.pathname}, tabIdx = ${tabIdx}`
      );

      if (tabIdx >= 0 && tabIdx !== value) {
        openTab(tabIdx);
        onCurrentTab && onCurrentTab(tabs[tabIdx]);
      } else {
        logger.info(`useEffect() [history.location.pathname] dont open`, value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname, tabs.length]);

  // slider options
  const slideOpts = {
    initialSlide: +value,
    speed: 400,
    spaceBetween: 0,
    allowTouchMove: true,
  };

  const isDesktopOrLaptop = useMediaQuery({ minWidth: 992 });

  const scrollableClass =
    scrollable === true ? 'scrollable scroll-y thin-scrollbar ' : ' ';

  if (isDesktopOrLaptop) {
    slideOpts.allowTouchMove = false;
  }

  // a ref variable to handle the current slider
  const slider = useRef<any>(null);

  // a function to handle the segment changes
  const handleSegmentChange = (e: any) => {
    const value = e.detail.value;
    openTab(value);
  };

  // a function to handle the slider changes
  const handleSlideChange = async (event: any) => {
    await event.target.getActiveIndex().then((value: number) => {
      setValue(value);
      if (!history.location.pathname.startsWith(tabs[value].link)) {
        history.replace(tabs[value].link);
        onCurrentTab && onCurrentTab(tabs[value]);
      }
    });
  };

  return (
    <div id={id} className="tabs">
      <IonToolbar
        className={
          classT +
          ' px-4 pr-0 md:px-14 h-10 flex items-center mb-4 scrollable-segments'
        }
      >
        <IonSegment
          className="w-full flex justify-start items-center mt-3"
          onIonChange={(e) => {
            handleSegmentChange(e);
          }}
          mode="md"
          value={`${value}`}
        >
          {tabs.map((tab, index) => (
            <TabButton key={index} value={index + ''} label={tab.label} />
          ))}
        </IonSegment>
      </IonToolbar>
      <IonSlides
        className=""
        pager={false}
        options={slideOpts}
        ref={slider}
        onIonSlideDidChange={(e) => handleSlideChange(e)}
      >
        {tabs.map((tab, index) => {
          return (
            <IonSlide key={index} className={scrollableClass + 'px-4 md:px-14'}>
              {tab.component}
            </IonSlide>
          );
        })}
      </IonSlides>
    </div>
  );
};
